import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiPhone, FiMail, FiMapPin, FiClock, FiMessageCircle, FiNavigation } = FiIcons;

const Contact = () => {
  const contactMethods = [
    {
      icon: FiPhone,
      title: "Call Us",
      primary: "+971503598111",
      secondary: "Available 9 AM - 10 PM",
      action: "tel:+971503598111"
    },
    {
      icon: FiMapPin,
      title: "Visit Our Store",
      primary: "Al Hudaiba Rd - Al Satwa",
      secondary: "Dubai - United Arab Emirates",
      action: "https://maps.app.goo.gl/DBLRAmgU6RETSxxH7"
    },
    {
      icon: FiClock,
      title: "Business Hours",
      primary: "Monday - Saturday: 9 AM - 10 PM",
      secondary: "Sunday: 10 AM - 9 PM",
      action: null
    }
  ];

  const operatingHours = [
    { day: "Monday", hours: "9:00 AM - 10:00 PM" },
    { day: "Tuesday", hours: "9:00 AM - 10:00 PM" },
    { day: "Wednesday", hours: "9:00 AM - 10:00 PM" },
    { day: "Thursday", hours: "9:00 AM - 10:00 PM" },
    { day: "Friday", hours: "9:00 AM - 11:00 PM" },
    { day: "Saturday", hours: "9:00 AM - 11:00 PM" },
    { day: "Sunday", hours: "10:00 AM - 9:00 PM" }
  ];

  const features = [
    {
      icon: FiMessageCircle,
      title: "Expert Consultation",
      description: "Get personalized advice from our knowledgeable staff to choose the right product for your needs."
    },
    {
      icon: FiNavigation,
      title: "Easy Location",
      description: "Conveniently located in Al Satwa with easy access and parking facilities for our customers."
    },
    {
      icon: FiPhone,
      title: "Quick Support",
      description: "Fast and efficient customer support for all your queries and after-sales service needs."
    }
  ];

  return (
    <div className="w-full overflow-x-hidden">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-red-600 via-red-700 to-red-800 text-white py-20 w-full">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
              Get In Touch
            </h1>
            <p className="text-xl text-red-100 max-w-3xl mx-auto leading-relaxed">
              Visit our store in Dubai or contact us for the best deals on mobile phones, electronics, and accessories. 
              Our friendly team is ready to help you find exactly what you need.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Methods */}
      <section className="py-20 w-full">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 w-full">
            {contactMethods.map((method, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="text-center p-8 bg-white rounded-xl shadow-lg hover:shadow-xl transition-shadow group"
              >
                <div className="bg-red-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:bg-red-600 transition-colors">
                  <SafeIcon icon={method.icon} className="w-8 h-8 text-red-600 group-hover:text-white transition-colors" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  {method.title}
                </h3>
                <p className="text-gray-800 font-medium mb-2">
                  {method.primary}
                </p>
                <p className="text-gray-600 mb-6">
                  {method.secondary}
                </p>
                {method.action && (
                  <a
                    href={method.action}
                    target={method.action.startsWith('http') ? '_blank' : '_self'}
                    rel={method.action.startsWith('http') ? 'noopener noreferrer' : ''}
                    className="inline-block bg-red-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-red-700 transition-colors"
                  >
                    {method.action.startsWith('tel:') ? 'Call Now' : 'Get Directions'}
                  </a>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Map and Store Info */}
      <section className="py-20 bg-gray-50 w-full">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 w-full">
            {/* Map */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="relative"
            >
              <div className="aspect-video rounded-2xl overflow-hidden shadow-xl">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3609.276082927204!2d55.2744691!3d25.227624499999997!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3e5f42f9bc44e0cb%3A0xf02b91c410fb5290!2zS2Fyb29uIFBob25lcyAmIEVsZWN0cm9uaWNzIHwg2YPYp9ix2YjZhiDZhNmE2YfZiNin2KrZgSDZiCDYp9mE2KfZhNmD2KrYsdmI2YbZitin2Ko!5e0!3m2!1sen!2s!4v1765371481519!5m2!1sen!2s"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  className="w-full h-full"
                ></iframe>
              </div>
            </motion.div>

            {/* Store Information */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="space-y-8"
            >
              <div>
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                  Visit Our Store
                </h2>
                <p className="text-lg text-gray-600 leading-relaxed mb-6">
                  Located in the heart of Al Satwa, Dubai, our store offers a complete shopping experience with the latest 
                  mobile phones, electronics, and accessories. Come visit us to see our products in person and get expert advice.
                </p>
              </div>

              {/* Operating Hours */}
              <div className="bg-white p-6 rounded-xl shadow-lg">
                <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                  <SafeIcon icon={FiClock} className="w-5 h-5 text-red-600 mr-3" />
                  Operating Hours
                </h3>
                <div className="space-y-2">
                  {operatingHours.map((schedule, index) => (
                    <div key={index} className="flex justify-between items-center py-2 border-b border-gray-100 last:border-0">
                      <span className="text-gray-700 font-medium">{schedule.day}</span>
                      <span className="text-gray-600">{schedule.hours}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Contact Info */}
              <div className="bg-white p-6 rounded-xl shadow-lg">
                <h3 className="text-xl font-semibold text-gray-900 mb-4">
                  Contact Information
                </h3>
                <div className="space-y-4">
                  <div className="flex items-start space-x-3">
                    <SafeIcon icon={FiMapPin} className="w-5 h-5 text-red-600 mt-1 flex-shrink-0" />
                    <div>
                      <p className="font-medium text-gray-900">Address</p>
                      <p className="text-gray-600">Al Hudaiba Rd - Al Satwa<br />Dubai - United Arab Emirates</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <SafeIcon icon={FiPhone} className="w-5 h-5 text-red-600 mt-1 flex-shrink-0" />
                    <div>
                      <p className="font-medium text-gray-900">Phone</p>
                      <p className="text-gray-600">+971503598111</p>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 w-full">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Why Visit Our Store?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Experience personalized service and see our products firsthand
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 w-full">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="text-center p-6"
              >
                <div className="bg-red-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <SafeIcon icon={feature.icon} className="w-8 h-8 text-red-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  {feature.title}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {feature.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-red-600 text-white w-full">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center w-full">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="space-y-6"
          >
            <h2 className="text-3xl md:text-4xl font-bold">
              Ready to Visit Us?
            </h2>
            <p className="text-xl text-red-100 max-w-3xl mx-auto">
              Come to our store for the best deals on mobile phones and electronics, or call us for any inquiries. 
              We're here to help you find the perfect device!
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="https://maps.app.goo.gl/DBLRAmgU6RETSxxH7"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-white text-red-600 px-8 py-3 rounded-lg font-semibold hover:bg-red-50 transition-colors"
              >
                Get Directions
              </a>
              <a
                href="tel:+971503598111"
                className="border-2 border-white text-white px-8 py-3 rounded-lg font-semibold hover:bg-white hover:text-red-600 transition-colors"
              >
                Call Now
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Contact;